import { Component } from '@theme/component';

/** Fraction of the remaining gap the cursor closes each frame. */
const EASING = 4;

/** Below this gap the cursor has arrived, and the frame loop can stop. */
const SETTLED_AT = 0.1;

class CustomCursorComponent extends Component {
  #posX = 0;
  #posY = 0;
  #mouseX = 0;
  #mouseY = 0;
  #rafId = 0;
  #seenPointer = false;
  /** @type {MediaQueryList | null} */
  #pointerQuery = null;

  connectedCallback() {
    super.connectedCallback();
    this.#pointerQuery = window.matchMedia('(pointer: fine)');
    this.#pointerQuery.addEventListener('change', this.#syncPointer, { signal: this.abortController.signal });
    this.#syncPointer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousemove', this.#handleMouseMove);
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }
  }

  /** Tracks the mouse only while a fine pointer is the active input. */
  #syncPointer = () => {
    document.removeEventListener('mousemove', this.#handleMouseMove);
    if (this.#pointerQuery?.matches) {
      document.addEventListener('mousemove', this.#handleMouseMove);
    } else {
      this.deactivate();
    }
  };

  #handleMouseMove = (event) => {
    this.#mouseX = event.clientX;
    this.#mouseY = event.clientY;
    this.#seenPointer = true;
    if (!this.classList.contains('active') || this.#rafId) return;
    this.#rafId = requestAnimationFrame(this.#animate);
  };

  #animate = () => {
    this.#rafId = 0;
    this.#posX += (this.#mouseX - this.#posX) / EASING;
    this.#posY += (this.#mouseY - this.#posY) / EASING;
    this.style.setProperty('--cursor-x', `${this.#posX}px`);
    this.style.setProperty('--cursor-y', `${this.#posY}px`);
    const settled = Math.abs(this.#mouseX - this.#posX) < SETTLED_AT
      && Math.abs(this.#mouseY - this.#posY) < SETTLED_AT;
    if (!settled && this.classList.contains('active')) {
      this.#rafId = requestAnimationFrame(this.#animate);
    }
  };

  /** Last pointer position seen. */
  get pointer() {
    return { x: this.#mouseX, y: this.#mouseY };
  }

  /** Whether a pointer has ever been seen, since (0, 0) is a real place. */
  get seenPointer() {
    return this.#seenPointer;
  }

  /** Shows the cursor. Returns false when a coarse pointer turns it down. */
  activate() {
    if (!this.#pointerQuery?.matches) return false;
    this.#posX = this.#mouseX;
    this.#posY = this.#mouseY;
    this.classList.add('active');
    if (!this.#rafId) this.#rafId = requestAnimationFrame(this.#animate);
    return true;
  }

  deactivate() {
    this.classList.remove('active');
  }
}

if (!customElements.get('custom-cursor')) {
  customElements.define('custom-cursor', CustomCursorComponent);
}

const MASK_CLASS = 'cursor-none';

/** Every live binding, so a closing overlay can hand the cursor back. */
const activeBindings = new Set();

/** How many bindings want the cursor showing. */
let cursorClaims = 0;

/** How many bindings want each mask class. */
const maskHolders = new WeakMap();

/** Adds or drops one claim on a mask class. Counts only claims made here — carousel.js toggles its own. */
function claimMask(element, className, delta) {
  let claims = maskHolders.get(element);
  if (!claims) maskHolders.set(element, (claims = new Map()));
  const next = Math.max(0, (claims.get(className) ?? 0) + delta);
  claims.set(className, next);
  element.classList.toggle(className, next > 0);
}

/** The cursor element, once upgraded. */
export function getCustomCursor() {
  const cursor = document.querySelector('custom-cursor');
  return cursor?.matches(':defined') ? cursor : null;
}

/** Lets callers pass a live getter wherever an element or a list is expected. */
function resolve(value) {
  return typeof value === 'function' ? value() : value;
}

/** Normalises the mask option into [element, className] pairs. */
function maskEntries(mask) {
  const list = resolve(mask);
  return (Array.isArray(list) ? list : [list])
    .map((entry) => (Array.isArray(entry) ? entry : [entry, MASK_CLASS]))
    .filter(([element]) => element);
}

/** Whether a root can still take the pointer. Opacity is not asked: a transition delay leaves an open overlay at 0. */
function rootIsLive(root) {
  if (root === document) return true;
  const style = getComputedStyle(root);
  if (style.pointerEvents === 'none' || style.visibility === 'hidden') return false;
  return typeof root.checkVisibility === 'function'
    ? root.checkVisibility()
    : root.getClientRects().length > 0;
}

/** The excluded region, which can be several boxes. */
function exceptElements(except) {
  const list = resolve(except);
  return (Array.isArray(list) ? list : [list]).filter(Boolean);
}

/** Whether the hit is this element or lives inside it. */
function isWithin(element, hit) {
  return element === hit || element.contains(hit);
}

/**
 * Shows the close-cursor over `root` but outside `except`, hiding the native cursor on every
 * masked element in step. Returns null when there is no cursor element.
 *
 * @param {object} options
 * @param {Element | Document} options.root - Element the pointer listeners attach to.
 * @param {Element | Element[] | (() => Element | Element[] | null) | null} [options.except] - Region(s) that turn the cursor back off.
 * @param {Element | Array | (() => Array)} [options.mask] - Elements that hide the native cursor in step.
 * @param {{x: number, y: number} | null} [options.at] - Point to classify from, for openers that know the click.
 */
export function bindCloseCursor({ root, except = null, mask = [], at = null } = {}) {
  const cursor = getCustomCursor();
  if (!root || !cursor) return null;

  const controller = new AbortController();
  const { signal } = controller;
  let inZone = null;
  let frame = 0;

  /** Claims and releases masks as a set, so a getter that changes cannot strand a class. */
  let heldMasks = [];
  const setMasks = (on) => {
    if (on === (heldMasks.length > 0)) return;
    if (on) {
      heldMasks = maskEntries(mask);
      for (const [element, className] of heldMasks) claimMask(element, className, 1);
      return;
    }
    for (const [element, className] of heldMasks) claimMask(element, className, -1);
    heldMasks = [];
  };

  /** Takes or gives back one claim on the shared cursor, tracked apart from `inZone` which a re-decision forgets. */
  let holding = false;
  const holdCursor = (on) => {
    if (on === holding) return true;
    if (!on) {
      holding = false;
      cursorClaims = Math.max(0, cursorClaims - 1);
      if (!cursorClaims) cursor.deactivate();
      return true;
    }
    if (!cursor.activate()) return false;
    holding = true;
    cursorClaims += 1;
    return true;
  };

  const apply = (next) => {
    if (next === inZone) return;
    if (next && !holdCursor(true)) return;
    if (!next) holdCursor(false);
    inZone = next;
    setMasks(next);
  };

  /** Asks what is painted under the point. Root before except: an overlay nested in the panel it excludes still owns its own area. */
  /** Whether another live binding's overlay covers this point, so a page-wide binding stands down. */
  const coveredByOther = (hit) => {
    for (const other of activeBindings) {
      if (other === handle || other.root === document) continue;
      if (isWithin(other.root, hit) && rootIsLive(other.root)) return true;
    }
    return false;
  };

  const decide = (x, y) => {
    if (root !== document && !rootIsLive(root)) return false;
    const excluded = exceptElements(except);
    const [hit] = document.elementsFromPoint(x, y);
    if (!hit) return root === document;
    if (root !== document && isWithin(root, hit)) return true;
    if (excluded.some((element) => isWithin(element, hit))) return false;
    return root === document && !coveredByOther(hit);
  };

  /** A move only reaches root because the browser hit-tested it there; only a document root, which hears every move, still has to work it out. */
  const draw = () => {
    frame = 0;
    const { x, y } = cursor.pointer;
    apply(root === document ? decide(x, y) : true);
  };

  const onMove = () => {
    frame ||= requestAnimationFrame(draw);
  };

  const leave = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    apply(false);
  };

  const { documentElement } = document;
  const leaveTarget = root === document ? documentElement : root;
  root.addEventListener('mousemove', onMove, { passive: true, signal });
  leaveTarget.addEventListener('mouseleave', leave, { signal });
  if (leaveTarget !== documentElement) documentElement.addEventListener('mouseleave', leave, { signal });

  /** Decides from where the pointer rests rather than from a move. An `at` of (0, 0) is a keyboard press, not a place. */
  const settle = (forget = false) => {
    if (forget) inZone = null;
    const opener = at && (at.x || at.y) ? at : null;
    if (!opener && !cursor.seenPointer) return;
    const { x, y } = opener ?? cursor.pointer;
    apply(decide(x, y));
  };

  const stop = () => {
    activeBindings.delete(handle);
    controller.abort();
    cancelAnimationFrame(frame);
    frame = 0;
    apply(false);
  };

  const handle = { root, stop, leave, revive: () => settle(true), signal };

  activeBindings.add(handle);
  settle();
  return handle;
}

/** Asks every live binding to re-decide. Call once a closing overlay is really gone, not while it animates out. */
export function reviveCloseCursors() {
  for (const handle of activeBindings) handle.revive();
}
